import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Input } from "reactstrap";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import ValetApi from "../api/Api";

function formatCents(cents, currency = "usd") {
    const value = Number(cents || 0) / 100;
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: String(currency || "usd").toUpperCase() }).format(value);
    } catch {
        return `$${value.toFixed(2)}`;
    }
}

const SUPPORTED_MANUAL_METHODS = ["cash", "void"];

const MANUAL_METHOD_LABELS = {
    cash: "Cash",
    void: "Void / Waived",
};

function getManualMethodsFromConfig(config) {
    const methods = Array.isArray(config?.manualMethods)
        ? config.manualMethods
              .map((method) =>
                  String(method || "")
                      .trim()
                      .toLowerCase(),
              )
              .filter((method) => SUPPORTED_MANUAL_METHODS.includes(method))
        : [];
    return methods.length ? Array.from(new Set(methods)) : ["cash"];
}

const PaymentCollectionModal = ({ isOpen, toggle, vehicleId, podiumId, onPaymentUpdated, allowManualFallback = false }) => {
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);
    const [statusData, setStatusData] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [manualMethod, setManualMethod] = useState("cash");
    const [manualNote, setManualNote] = useState("");
    const [isManualSettling, setIsManualSettling] = useState(false);

    useEffect(() => {
        async function fetchStatus() {
            if (!isOpen || !vehicleId || !podiumId) return;

            try {
                setIsLoadingStatus(true);
                setLoadError("");
                const res = await ValetApi.getVehiclePaymentStatus(vehicleId, podiumId);
                const status = res?.data?.status || null;
                setStatusData(status);
                const methods = getManualMethodsFromConfig(status?.config);
                setManualMethod(methods[0] || "cash");
            } catch (err) {
                setStatusData(null);
                setLoadError(err?.[0] || err?.response?.data?.error?.message || "Unable to initialize payment");
            } finally {
                setIsLoadingStatus(false);
            }
        }

        fetchStatus();
    }, [isOpen, vehicleId, podiumId]);

    async function handleManualSettle() {
        if (!vehicleId || !podiumId || isManualSettling) return;

        const amountDueCents = Number(statusData?.amountDueCents || 0);

        try {
            setIsManualSettling(true);
            await ValetApi.settleVehiclePaymentInPerson(vehicleId, podiumId, {
                method: manualMethod,
                note: manualNote,
                amountPaidCents: amountDueCents,
            });
            toast.success("Payment recorded as in-person", { autoClose: 1200 });
            toggle();
            if (typeof onPaymentUpdated === "function") {
                onPaymentUpdated();
            }
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to mark payment as in-person", { autoClose: 1500 });
        } finally {
            setIsManualSettling(false);
        }
    }

    const amountDueCents = Number(statusData?.amountDueCents || 0);
    const currency = statusData?.currency || "usd";
    const isPaid = Boolean(statusData?.isPaid || statusData?.paymentStatus === "paid");
    const isPaymentFeatureEnabled = Boolean(statusData?.featureEnabled === true);
    const manualMethods = getManualMethodsFromConfig(statusData?.config);

    const customerPayUrl = useMemo(() => {
        const ticketNum = statusData?.ticketNum;
        const customerToken = statusData?.customerToken;
        if (!ticketNum || !customerToken || typeof window === "undefined") return "";
        return `${window.location.origin}/customers/${encodeURIComponent(ticketNum)}/${encodeURIComponent(customerToken)}`;
    }, [statusData?.ticketNum, statusData?.customerToken]);

    async function copyCustomerPayLink() {
        if (!customerPayUrl) return;

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(customerPayUrl);
                toast.success("Customer payment link copied", { autoClose: 1200 });
                return;
            }

            const tempInput = document.createElement("input");
            tempInput.value = customerPayUrl;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand("copy");
            document.body.removeChild(tempInput);
            toast.success("Customer payment link copied", { autoClose: 1200 });
        } catch (err) {
            toast.error("Unable to copy payment link", { autoClose: 1400 });
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            modalClassName="garage-payment-modal-theme"
            centered>
            <ModalHeader toggle={toggle}>Collect Payment</ModalHeader>
            <ModalBody>
                {isLoadingStatus ? <Spinner color="primary" /> : null}

                {!isLoadingStatus && loadError ? <div className="payment-collection-error">{loadError}</div> : null}

                {!isLoadingStatus && !loadError && !isPaymentFeatureEnabled ? <div className="payment-collection-disabled">Payments are disabled in this environment.</div> : null}

                {!isLoadingStatus && !loadError && isPaid ? <div className="payment-collection-paid">This visit is already paid.</div> : null}

                {!isLoadingStatus && !loadError && isPaymentFeatureEnabled && !isPaid ? (
                    <div className="payment-collection-manual">
                        <div className="payment-collection-manual-title">Fast Checkout: Customer Phone</div>
                        <div className="payment-collection-amount">Amount due: {formatCents(amountDueCents, currency)}</div>
                        <div className="payment-collection-manual-note">Valet does not collect card details here. Have the customer scan this QR and pay on their own phone using the customer payment page.</div>
                        {customerPayUrl ? (
                            <>
                                <div className="d-flex justify-content-center my-2">
                                    <QRCodeSVG
                                        value={customerPayUrl}
                                        size={160}
                                        level="M"
                                        includeMargin
                                    />
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => window.open(customerPayUrl, "_blank", "noopener,noreferrer")}
                                        type="button">
                                        Open Pay Page
                                    </Button>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={copyCustomerPayLink}
                                        type="button">
                                        Copy Link
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="payment-collection-error">Customer link unavailable for this visit.</div>
                        )}
                    </div>
                ) : null}

                {!isLoadingStatus && !loadError && isPaymentFeatureEnabled && !isPaid && allowManualFallback ? (
                    <div className="payment-collection-manual">
                        <div className="payment-collection-manual-title">Backup: Accept In-Person Payment</div>
                        <div className="payment-collection-manual-note">Use this only when payment was collected outside this screen.</div>
                        <Input
                            type="select"
                            value={manualMethod}
                            onChange={(e) => setManualMethod(e.target.value)}>
                            {manualMethods.map((method) => (
                                <option
                                    key={method}
                                    value={method}>
                                    {MANUAL_METHOD_LABELS[method] || method}
                                </option>
                            ))}
                        </Input>
                        <Input
                            type="text"
                            value={manualNote}
                            onChange={(e) => setManualNote(e.target.value)}
                            placeholder="Optional note"
                            maxLength={200}
                            className="payment-collection-manual-input"
                        />
                        <Button
                            color="warning"
                            className="payment-collection-manual-btn"
                            onClick={handleManualSettle}
                            disabled={isManualSettling}>
                            {isManualSettling ? "Saving..." : "Mark Paid In Person"}
                        </Button>
                    </div>
                ) : null}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    onClick={toggle}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PaymentCollectionModal;
